import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OnboardingLayout } from '../Layout';
import { Button } from '@/components/ui/Button';
import { CountryPicker } from '@/components/domain/CountryPicker';
import { ListItem } from '@/components/ui/ListItem';
import { useUpdateBookingPriorities } from '@/api/queries/user';
import { paths } from '@/routes/paths';
import { IcPlus, IcTrash } from '@/components/ui/icons';
import { BottomSheet } from '@/components/ui/BottomSheet';

export function OnboardingEvacuationCountriesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdateBookingPriorities();

  const [picked, setPicked] = useState<Array<{ code: string; name: string }>>([]);
  const [open, setOpen] = useState(false);

  const submit = async () => {
    await update.mutateAsync(
      picked.map((p, i) => ({ country_code: p.code, position: i })),
    );
    navigate(paths.onboardingSubscribePitch);
  };

  return (
    <OnboardingLayout back title={t('onboarding.evacuation.additional_countries')}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {picked.length === 0 && (
          <div style={{ color: 'var(--color-text-on-dark-muted)' }}>
            {t('onboarding.evacuation.additional_empty')}
          </div>
        )}
        {picked.map((p, i) => (
          <ListItem
            key={p.code}
            leading={<span>{i + 1}</span>}
            title={p.name}
            subtitle={p.code}
            trailing={
              <button
                aria-label="Remove"
                style={{ color: 'var(--color-danger)' }}
                onClick={() => setPicked((cur) => cur.filter((x) => x.code !== p.code))}
              >
                <IcTrash />
              </button>
            }
          />
        ))}
      </div>
      <Button block variant="inverted" startIcon={<IcPlus />} onClick={() => setOpen(true)} disabled={picked.length >= 10}>
        {t('common.add')}
      </Button>
      <Button block size="lg" variant="ghost" onClick={submit} loading={update.isPending}>
        {t('common.continue')}
      </Button>

      <BottomSheet open={open} onClose={() => setOpen(false)} title={t('settings.additional_countries')}>
        <CountryPicker
          exclude={picked.map((p) => p.code)}
          onPick={(code, name) => {
            setPicked((cur) => [...cur, { code, name }]);
            setOpen(false);
          }}
        />
      </BottomSheet>
    </OnboardingLayout>
  );
}
